import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, TextField, InputAdornment } from '@mui/material';
import { Phone } from '@mui/icons-material';
import { CountryDial, COUNTRY_DIALS, splitPhoneValue, combinePhone, normalizeDial, filterCountryDials, FlagIcon, maxDigitsFor, limitPhoneDigits } from './PhoneNumberInput';

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  defaultDial?: string;
  size?: 'small' | 'medium';
}

/** MUI-styled sibling of PhoneNumberInput.tsx, for the two Add User forms built with MUI
 * TextField/Select rather than this codebase's plain inline-token inputs. Uses MUI's own
 * `Autocomplete` with `freeSolo` -- clicking shows the full country list, typing filters it by
 * name/ISO/dial code, and a typed value matching nothing is still accepted as a custom dial code.
 * Shares the same country data and state-handling approach as PhoneNumberInput.tsx (see that
 * file's doc comment), and still hands the parent a single combined "+<code> <number>" string. */
export default function MuiPhoneNumberInput({ value, onChange, label = 'Phone Number (Optional)', defaultDial = '+1', size }: Props) {
  const initial = useMemo(() => splitPhoneValue(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dial, setDial] = useState(initial.dial || defaultDial);
  const [rest, setRest] = useState(initial.rest);
  const [query, setQuery] = useState(initial.dial || defaultDial);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const parsed = splitPhoneValue(value);
    const d = parsed.dial || defaultDial;
    setDial(d);
    setRest(parsed.rest);
    setQuery(d);
    lastEmitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (d: string, r: string) => {
    const combined = combinePhone(d, r);
    lastEmitted.current = combined;
    onChange(combined);
  };

  const currentOption = useMemo(() => COUNTRY_DIALS.find(c => c.dial === dial) || null, [dial]);

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Autocomplete
        freeSolo
        options={COUNTRY_DIALS}
        getOptionLabel={opt => (typeof opt === 'string' ? opt : `${opt.name} (${opt.dial})`)}
        filterOptions={(options, state) => filterCountryDials(state.inputValue) as CountryDial[]}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.iso} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              <FlagIcon iso={option.iso} width={18} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.name}</span>
            </Box>
            <span style={{ color: '#94a3b8', flexShrink: 0 }}>{option.dial}</span>
          </Box>
        )}
        value={currentOption}
        inputValue={query}
        onInputChange={(_e, newInputValue, reason) => {
          if (reason === 'reset') return; // the onChange handler below already committed this
          setQuery(newInputValue);
        }}
        onChange={(_e, newVal) => {
          if (!newVal) return;
          const d = typeof newVal === 'string' ? normalizeDial(newVal) : newVal.dial;
          setDial(d);
          setQuery(d);
          emit(d, rest);
        }}
        onBlur={() => {
          if (query === dial) return;
          const d = normalizeDial(query) || dial;
          setDial(d);
          setQuery(d);
          emit(d, rest);
        }}
        size={size}
        sx={{ width: 190 }}
        renderInput={params => (
          <TextField {...params} placeholder="+1" size={size}
            inputProps={{ ...params.inputProps, onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select() }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                  <FlagIcon iso={currentOption?.iso} width={18} />
                </InputAdornment>
              ),
            }} />
        )}
      />
      <TextField
        fullWidth
        label={label}
        value={rest}
        size={size}
        onChange={e => {
          const limited = limitPhoneDigits(e.target.value, maxDigitsFor(currentOption?.iso));
          setRest(limited);
          emit(dial, limited);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone sx={{ fontSize: 20, color: '#94a3b8' }} />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
